// SQL Parser for PostgreSQL DDL
// Parses CREATE TABLE, CREATE VIEW, FOREIGN KEYS, PRIMARY KEYS, AUTO-INCREMENT, COMMENTS, etc.

/**
 * Clean quotes and brackets from identifiers
 */
const cleanIdentifier = (str) => {
  if (!str) return '';
  return str.replace(/["`\[\]]/g, '').trim();
};

/**
 * Main parser function for SQL DDL content
 */
export const parseSQLDDL = (sqlContent) => {
  if (!sqlContent) return { tables: [], views: [], relationships: [] };

  const tables = [];
  const views = [];
  const relationships = [];

  // 1. Parse COMMENT ON statements first (handles multi-line comments & escaped quotes)
  const comments = parseComments(sqlContent);

  // 2. Prepare clean SQL (strip block comments /* ... */ and line comments -- ...)
  const cleanSQL = removeSQLComments(sqlContent);

  // 3. Parse CREATE TABLE statements
  const tableRegex = /CREATE TABLE\s+(?:IF NOT EXISTS\s+)?(?:([`"\[]?[\w]+[`"\]]?)\.)?([`"\[]?[\w]+[`"\]]?)\s*\(([\s\S]*?)\);/gi;
  let match;

  while ((match = tableRegex.exec(cleanSQL)) !== null) {
    const rawSchema = match[1];
    const rawTableName = match[2];
    const tableBody = match[3];

    const schema = rawSchema ? cleanIdentifier(rawSchema) : '';
    const tableName = cleanIdentifier(rawTableName);

    const fields = [];
    const pkSet = new Set();
    const ukSet = new Set();
    const tableChecks = [];

    // Split by commas (respecting parentheses)
    const parts = splitByComma(tableBody);

    for (const part of parts) {
      const trimmed = part.trim();
      if (!trimmed) continue;

      const upper = trimmed.toUpperCase();

      // Check if this part is a table-level CONSTRAINT
      if (
        upper.startsWith('CONSTRAINT') ||
        upper.startsWith('PRIMARY KEY') ||
        upper.startsWith('FOREIGN KEY') ||
        upper.startsWith('UNIQUE') ||
        upper.startsWith('CHECK')
      ) {
        // Table-level PRIMARY KEY constraint
        if (upper.includes('PRIMARY KEY')) {
          const pkMatch = trimmed.match(/PRIMARY KEY\s*\(([^)]+)\)/i);
          if (pkMatch) {
            pkMatch[1].split(',').forEach(col => {
              const cleanCol = cleanIdentifier(col);
              if (cleanCol) pkSet.add(cleanCol);
            });
          }
        }

        // Table-level FOREIGN KEY constraint
        if (upper.includes('FOREIGN KEY')) {
          const fkMatch = trimmed.match(
            /FOREIGN KEY\s*\(([^)]+)\)\s*REFERENCES\s+(?:([`"\[]?[\w]+[`"\]]?)\.)?([`"\[]?[\w]+[`"\]]?)\s*\(([^)]+)\)/i
          );
          if (fkMatch) {
            const srcFields = fkMatch[1].split(',').map(f => cleanIdentifier(f));
            const tgtTable = cleanIdentifier(fkMatch[3]);
            const tgtFields = fkMatch[4].split(',').map(f => cleanIdentifier(f));

            srcFields.forEach((srcField, i) => {
              relationships.push({
                sourceTable: tableName,
                sourceField: srcField,
                targetTable: tgtTable,
                targetField: tgtFields[i] || tgtFields[0]
              });
            });
          }
        }

        // Table-level UNIQUE constraint
        if (upper.includes('UNIQUE') && !upper.includes('PRIMARY KEY')) {
          const ukMatch = trimmed.match(/UNIQUE\s*\(([^)]+)\)/i);
          if (ukMatch) {
            ukMatch[1].split(',').forEach(col => {
              const cleanCol = cleanIdentifier(col);
              if (cleanCol) ukSet.add(cleanCol);
            });
          }
        }

        // Table-level CHECK constraint
        if (upper.includes('CHECK')) {
          const checkMatch = trimmed.match(/CHECK\s*\(([^)]+)\)/i);
          if (checkMatch) {
            tableChecks.push(checkMatch[1].trim());
          }
        }
      } else {
        // Column definition
        const field = parseColumnDefinition(trimmed);
        if (field) {
          if (field.isPrimaryKey) {
            pkSet.add(field.name);
          }

          // Attach column comment if available
          const commentLookup = comments[tableName] || (schema ? comments[`${schema}.${tableName}`] : null);
          if (commentLookup && commentLookup[field.name]) {
            field.description = commentLookup[field.name];
          }

          fields.push(field);
        }
      }
    }

    // Post-pass: Apply primary key, unique, and table-level check constraint flags to fields
    fields.forEach(field => {
      if (pkSet.has(field.name)) {
        field.isPrimaryKey = true;
        field.nullable = false;
      }
      if (ukSet.has(field.name)) {
        field.isUnique = true;
      }
    });

    tableChecks.forEach(checkExpr => {
      const targetField = fields.find(f => new RegExp(`\\b${f.name}\\b`, 'i').test(checkExpr));
      if (targetField && !targetField.checkConstraint) {
        targetField.checkConstraint = checkExpr;
      }
    });

    const tableComment = comments[tableName]?._table || (schema ? comments[`${schema}.${tableName}`]?._table : '') || '';

    tables.push({
      name: tableName,
      schema: schema,
      description: tableComment,
      fields
    });
  }

  // 4. Parse ALTER TABLE ... ADD CONSTRAINT for Primary Keys defined outside CREATE TABLE
  const alterTablePKRegex = /ALTER TABLE\s+(?:ONLY\s+)?(?:([`"\[]?[\w]+[`"\]]?)\.)?([`"\[]?[\w]+[`"\]]?)\s+ADD\s+(?:CONSTRAINT\s+[^\s]+\s+)?PRIMARY KEY\s*\(([^)]+)\)/gi;
  while ((match = alterTablePKRegex.exec(cleanSQL)) !== null) {
    const targetTableName = cleanIdentifier(match[2]);
    const pkCols = match[3].split(',').map(c => cleanIdentifier(c));

    const tableObj = tables.find(t => t.name.toLowerCase() === targetTableName.toLowerCase());
    if (tableObj) {
      tableObj.fields.forEach(field => {
        if (pkCols.some(c => c.toLowerCase() === field.name.toLowerCase())) {
          field.isPrimaryKey = true;
          field.nullable = false;
        }
      });
    }
  }

  // 5. Parse ALTER TABLE ... ADD CONSTRAINT for Foreign Keys defined outside CREATE TABLE
  const alterTableFKRegex = /ALTER TABLE\s+(?:ONLY\s+)?(?:([`"\[]?[\w]+[`"\]]?)\.)?([`"\[]?[\w]+[`"\]]?)\s+ADD\s+(?:CONSTRAINT\s+[^\s]+\s+)?FOREIGN KEY\s*\(([^)]+)\)\s+REFERENCES\s+(?:([`"\[]?[\w]+[`"\]]?)\.)?([`"\[]?[\w]+[`"\]]?)\s*\(([^)]+)\)/gi;
  while ((match = alterTableFKRegex.exec(cleanSQL)) !== null) {
    const srcTable = cleanIdentifier(match[2]);
    const srcFields = match[3].split(',').map(f => cleanIdentifier(f));
    const tgtTable = cleanIdentifier(match[5]);
    const tgtFields = match[6].split(',').map(f => cleanIdentifier(f));

    srcFields.forEach((srcField, i) => {
      relationships.push({
        sourceTable: srcTable,
        sourceField: srcField,
        targetTable: tgtTable,
        targetField: tgtFields[i] || tgtFields[0]
      });
    });
  }

  // 6. Parse CREATE VIEW statements
  const viewRegex = /CREATE\s+(?:OR REPLACE\s+)?(?:MATERIALIZED\s+)?VIEW\s+(?:([`"\[]?[\w]+[`"\]]?)\.)?([`"\[]?[\w]+[`"\]]?)\s+AS\s+([\s\S]*?)(?=CREATE|COMMENT|ALTER|$)/gi;
  while ((match = viewRegex.exec(cleanSQL)) !== null) {
    const rawSchema = match[1];
    const rawViewName = match[2];
    const viewSQL = match[3].trim().replace(/;$/, '');
    const schema = rawSchema ? cleanIdentifier(rawSchema) : '';
    const viewName = cleanIdentifier(rawViewName);

    const viewComment = comments[viewName]?._table || (schema ? comments[`${schema}.${viewName}`]?._table : '') || '';

    views.push({
      name: viewName,
      schema: schema,
      description: viewComment,
      sqlDefinition: viewSQL
    });
  }

  // 7. Flag foreign key fields on tables based on relationships
  relationships.forEach(rel => {
    const tableObj = tables.find(t => t.name.toLowerCase() === rel.sourceTable.toLowerCase());
    if (tableObj) {
      const fieldObj = tableObj.fields.find(f => f.name.toLowerCase() === rel.sourceField.toLowerCase());
      if (fieldObj) {
        fieldObj.isForeignKey = true;
      }
    }
  });

  return { tables, views, relationships };
};

/**
 * Extract COMMENT ON TABLE and COMMENT ON COLUMN statements
 */
const parseComments = (sqlContent) => {
  const comments = {};
  const commentRegex = /COMMENT ON\s+(TABLE|COLUMN)\s+([a-z0-9_".]+)\s+IS\s+((?:'[^']*'\s*)+);?/gi;
  let match;

  while ((match = commentRegex.exec(sqlContent)) !== null) {
    const type = match[1].toUpperCase();
    const rawTarget = match[2].replace(/"/g, '');
    const rawLiteral = match[3];

    // Extract text inside single quotes (handling escaped quotes '')
    const commentText = (rawLiteral.match(/'(?:''|[^'])*'/g) || [])
      .map(str => str.slice(1, -1).replace(/''/g, "'"))
      .join('');

    const parts = rawTarget.split('.');
    let tableName = '';
    let columnName = '';

    if (type === 'TABLE') {
      tableName = parts.length > 1 ? parts[1] : parts[0];
      if (!comments[tableName]) comments[tableName] = {};
      comments[tableName]._table = commentText;
    } else if (type === 'COLUMN') {
      if (parts.length >= 3) {
        tableName = parts[1];
        columnName = parts[2];
      } else if (parts.length === 2) {
        tableName = parts[0];
        columnName = parts[1];
      }
      if (tableName && columnName) {
        if (!comments[tableName]) comments[tableName] = {};
        comments[tableName][columnName] = commentText;
      }
    }
  }

  return comments;
};

/**
 * Remove inline and block SQL comments
 */
const removeSQLComments = (sqlContent) => {
  return sqlContent
    .replace(/\/\*[\s\S]*?\*\//g, '') // remove /* ... */ block comments
    .split('\n')
    .map(line => {
      const commentIdx = line.indexOf('--');
      if (commentIdx === -1) return line;
      // Strip line comment if -- is not inside quotes
      const before = line.slice(0, commentIdx);
      const quoteCount = (before.match(/'/g) || []).length;
      if (quoteCount % 2 === 0) {
        return before;
      }
      return line;
    })
    .join('\n');
};

/**
 * Split comma-separated tokens respecting parentheses
 */
const splitByComma = (text) => {
  const parts = [];
  let current = '';
  let parenthesesLevel = 0;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];

    if (char === '(') {
      parenthesesLevel++;
      current += char;
    } else if (char === ')') {
      parenthesesLevel--;
      current += char;
    } else if (char === ',' && parenthesesLevel === 0) {
      parts.push(current);
      current = '';
    } else {
      current += char;
    }
  }

  if (current.trim()) {
    parts.push(current);
  }

  return parts;
};

/**
 * Parse column definition line into field object
 */
const parseColumnDefinition = (def) => {
  const defWithoutComment = def.split('--')[0].trim();
  if (!defWithoutComment) return null;

  // Match column name (quoted or unquoted), type, and rest of constraints
  const match = defWithoutComment.match(/^("?[a-z0-9_]+"?)\s+([a-z0-9_()[\]]+)(?:\s+(.*))?$/i);
  if (!match) return null;

  let [, name, type, constraints = ''] = match;
  name = name.replace(/"/g, '').trim();

  // Handle multi-word data types (e.g., DOUBLE PRECISION, CHARACTER VARYING, TIMESTAMP WITH TIME ZONE)
  const multiWordTypes = ['PRECISION', 'VARYING', 'WITH TIME ZONE', 'WITHOUT TIME ZONE'];
  for (const mw of multiWordTypes) {
    if (constraints.toUpperCase().startsWith(mw)) {
      type += ' ' + mw;
      constraints = constraints.slice(mw.length).trim();
      break;
    }
  }

  const fieldType = mapPostgreSQLType(type);
  const upperConstraints = constraints.toUpperCase();
  const upperType = type.toUpperCase();

  const isPrimaryKey = upperConstraints.includes('PRIMARY KEY') || upperType.includes('PRIMARY KEY');

  // Check for Auto-Increment / Identity keywords
  const isAutoIncrement =
    upperType.includes('SERIAL') ||
    upperConstraints.includes('IDENTITY') ||
    upperConstraints.includes('AUTO_INCREMENT') ||
    upperConstraints.includes('AUTOINCREMENT') ||
    upperConstraints.includes('GENERATED');

  const isUnique = upperConstraints.includes('UNIQUE');
  const isNotNull = upperConstraints.includes('NOT NULL') || isPrimaryKey || isAutoIncrement;

  let checkConstraint = '';
  const checkMatch = constraints.match(/CHECK\s*\(([^)]+)\)/i);
  if (checkMatch) {
    checkConstraint = checkMatch[1].trim();
  }

  return {
    name,
    type: fieldType,
    nullable: !isNotNull,
    isPrimaryKey,
    isUnique,
    autoIncrement: isAutoIncrement,
    isForeignKey: false,
    description: '',
    checkConstraint
  };
};

/**
 * Map PostgreSQL data types to internal SchemaGraphos data types
 */
export const mapPostgreSQLType = (pgType) => {
  const typeUpper = pgType.toUpperCase();

  // Remove size specifications and array brackets
  const baseType = typeUpper.split('(')[0].split('[')[0].trim();

  const typeMap = {
    // String types
    'VARCHAR': 'VARCHAR',
    'CHAR': 'CHAR',
    'TEXT': 'TEXT',
    'CHARACTER': 'VARCHAR',

    // Numeric types
    'INTEGER': 'INTEGER',
    'INT': 'INTEGER',
    'INT4': 'INTEGER',
    'INT8': 'BIGINT',
    'BIGINT': 'BIGINT',
    'SMALLINT': 'SMALLINT',
    'INT2': 'SMALLINT',
    'SERIAL': 'INTEGER',
    'BIGSERIAL': 'BIGINT',
    'NUMERIC': 'DECIMAL',
    'DECIMAL': 'DECIMAL',
    'REAL': 'FLOAT',
    'FLOAT': 'FLOAT',
    'FLOAT4': 'FLOAT',
    'FLOAT8': 'DOUBLE',
    'DOUBLE': 'DOUBLE',

    // Boolean
    'BOOLEAN': 'BOOLEAN',
    'BOOL': 'BOOLEAN',

    // Date/Time
    'DATE': 'DATE',
    'TIME': 'TIME',
    'TIMESTAMP': 'TIMESTAMP',
    'TIMESTAMPTZ': 'TIMESTAMP',

    // UUID
    'UUID': 'UUID',

    // JSON
    'JSON': 'JSON',
    'JSONB': 'JSONB',

    // XML
    'XML': 'XML',

    // Spatial types
    'GEOMETRY': 'GEOMETRY',
    'GEOGRAPHY': 'GEOGRAPHY',
    'POINT': 'GEOMETRY',
    'LINESTRING': 'GEOMETRY',
    'POLYGON': 'GEOMETRY',

    // Array
    'ARRAY': 'TEXT',

    // Other
    'BYTEA': 'BLOB'
  };

  return typeMap[baseType] || 'VARCHAR';
};

export default {
  parseSQLDDL,
  mapPostgreSQLType
};
